import React from 'react';

import { css } from '@emotion/css';
import { SearchPlanningDTO } from '@recurrency/core-api-schema/dist/search/getSearchPlanning';

import { FlexSpace } from 'components/FlexSpace';
import { BorderedPropertyItem } from 'components/recipes/equation/BorderedPropertyItem';
import { Typography } from 'components/Typography';

import { formatUSD } from 'utils/formatting';

export const ProjectedInventoryChangeEquation = ({
  record,
  context = 'planning',
}: {
  record: SearchPlanningDTO;
  context?: 'planning' | 'dailyWorkflow';
}) => {
  const isOpOq = record.recurrency_replenishment_method === 'OP/OQ';

  // OP/OQ requires a different projected inventory change formula and tooltip
  // it's also different in how Backend calculates this value for planning
  // and how Daily Workflow manages this value dynamically in front end
  if (isOpOq) {
    if (context === 'dailyWorkflow') {
      // Daily Workflow OP/OQ formula: Unit Cost × ([New OP + (New OQ/2)] - [Current OP + (Current OQ/2)])
      return (
        <div>
          <FlexSpace direction="row" alignItems="center">
            <BorderedPropertyItem
              label="Unit Cost"
              value={formatUSD(record.unit_cost, true)}
              className={css`
                margin-top: auto;
                margin-bottom: auto;
              `}
            />
            <Typography type="large">×</Typography>
            <Typography type="h2">(</Typography>
            <Typography type="h2">[</Typography>
            <FlexSpace direction="row" alignItems="center">
              <BorderedPropertyItem
                label="New OP"
                value={`${record.recommended_min} ${record.unit_of_measure}`}
                className={css`
                  margin-top: auto;
                  margin-bottom: auto;
                `}
              />
              <Typography type="large">+</Typography>
              <FlexSpace
                direction="column"
                className={css`
                  align-items: center;
                  justify-items: center;
                `}
              >
                <BorderedPropertyItem
                  label="New OQ"
                  value={`${record.recommended_max} ${record.unit_of_measure}`}
                  className={css`
                    margin-top: auto;
                    margin-bottom: auto;
                  `}
                />
                <div
                  className={css`
                    border-bottom: 2px solid black;
                    width: 100%;
                    margin: 4px 0;
                  `}
                />
                <Typography type="large">2</Typography>
              </FlexSpace>
            </FlexSpace>
            <Typography type="h2">]</Typography>
            <Typography type="large">-</Typography>
            <Typography type="h2">[</Typography>
            <FlexSpace direction="row" alignItems="center">
              <BorderedPropertyItem
                label="Current OP"
                value={`${record.current_min} ${record.unit_of_measure}`}
                className={css`
                  margin-top: auto;
                  margin-bottom: auto;
                `}
              />
              <Typography type="large">+</Typography>
              <FlexSpace
                direction="column"
                className={css`
                  align-items: center;
                  justify-items: center;
                `}
              >
                <BorderedPropertyItem
                  label="Current OQ"
                  value={`${record.current_max} ${record.unit_of_measure}`}
                  className={css`
                    margin-top: auto;
                    margin-bottom: auto;
                  `}
                />
                <div
                  className={css`
                    border-bottom: 2px solid black;
                    width: 100%;
                    margin: 4px 0;
                  `}
                />
                <Typography type="large">2</Typography>
              </FlexSpace>
            </FlexSpace>
            <Typography type="h2">]</Typography>
            <Typography type="h2">)</Typography>
            <Typography type="large">=</Typography>
            <BorderedPropertyItem
              label="Projected Inventory Change"
              value={`${formatUSD(record.projected_inventory_change, true)}`}
              className={css`
                margin-top: auto;
                margin-bottom: auto;
              `}
            />
          </FlexSpace>
        </div>
      );
    }
    // Planning OP/OQ formula: Unit Cost × (Recommended OP - Current OP)
    return (
      <div>
        <FlexSpace direction="row">
          <BorderedPropertyItem
            label="Unit Cost"
            value={formatUSD(record.unit_cost, true)}
            className={css`
              margin-top: auto;
              margin-bottom: auto;
            `}
          />
          <Typography type="large">×</Typography>
          <Typography type="h2">(</Typography>
          <BorderedPropertyItem label="Recommended OP" value={`${record.recommended_min} ${record.unit_of_measure}`} />
          <Typography type="large">-</Typography>
          <BorderedPropertyItem label="Current OP" value={`${record.current_min} ${record.unit_of_measure}`} />
          <Typography type="h2">)</Typography>
          <Typography type="large">=</Typography>
          <BorderedPropertyItem
            label="Projected Inventory Change"
            value={`${formatUSD(record.projected_inventory_change, true)}`}
            className={css`
              margin-top: auto;
              margin-bottom: auto;
            `}
          />
        </FlexSpace>
      </div>
    );
  }

  // Min/Max formula: Unit Cost × ((Recommended Min + Recommended Max) / 2 - (Current Min + Current Max) / 2)
  return (
    <div>
      <FlexSpace direction="row">
        <BorderedPropertyItem
          label="Unit Cost"
          value={formatUSD(record.unit_cost, true)}
          className={css`
            margin-top: auto;
            margin-bottom: auto;
          `}
        />
        <Typography type="large">×</Typography>
        <Typography type="h2">(</Typography>
        <Typography type="h2">(</Typography>
        <FlexSpace
          direction="column"
          className={css`
            align-items: center;
            justify-items: center;
          `}
        >
          <FlexSpace
            direction="row"
            className={css`
              border-bottom: 2px solid black;
              padding-bottom: 8px;
            `}
          >
            <BorderedPropertyItem
              label="Recommended Min"
              value={`${record.recommended_min} ${record.unit_of_measure}`}
            />
            <div
              className={css`
                align-self: stretch;
                padding-top: 16px;
              `}
            >
              <Typography type="large">+</Typography>
            </div>
            <BorderedPropertyItem
              label="Recommended Max"
              value={`${record.recommended_max} ${record.unit_of_measure}`}
            />
          </FlexSpace>
          <Typography type="large">2</Typography>
        </FlexSpace>
        <Typography type="h2">)</Typography>
        <Typography type="large">-</Typography>
        <Typography type="h2">(</Typography>
        <FlexSpace
          direction="column"
          className={css`
            align-items: center;
            justify-items: center;
          `}
        >
          <FlexSpace
            direction="row"
            className={css`
              border-bottom: 2px solid black;
              padding-bottom: 8px;
            `}
          >
            <BorderedPropertyItem label="Current Min" value={`${record.current_min} ${record.unit_of_measure}`} />
            <div
              className={css`
                align-self: stretch;
                padding-top: 16px;
              `}
            >
              <Typography type="large">+</Typography>
            </div>
            <BorderedPropertyItem label="Current Max" value={`${record.current_max} ${record.unit_of_measure}`} />
          </FlexSpace>
          <Typography type="large">2</Typography>
        </FlexSpace>
        <Typography type="h2">)</Typography>
        <Typography type="h2">)</Typography>
        <Typography type="large">=</Typography>
        <BorderedPropertyItem
          label="Projected Inventory Change"
          value={`${formatUSD(record.projected_inventory_change, true)}`}
          className={css`
            margin-top: auto;
            margin-bottom: auto;
          `}
        />
      </FlexSpace>
    </div>
  );
};
